/**
 * Rubric vocabulary — the one place that turns a level, a domain key, or a
 * skill key into something a parent can read.
 *
 * The rubric artifact is agent-authored and carries only numbers and names.
 * What a level actually *means* for a given skill lives in the framework in
 * @aurora/core, which is static, reviewed prose. Every reader here degrades to
 * undefined rather than throwing when the agent names something the framework
 * has never heard of.
 */

import {
  DOMAINS,
  LEVEL_LABELS,
  getSkill,
  getSkillsForDomain,
  type DomainDef,
  type DomainKey,
  type Level,
  type LevelDescriptor,
  type Skill,
} from '@aurora/core'

const DOMAIN_KEYS = new Set(DOMAINS.map((d) => d.key as string))

export function isKnownDomain(key?: string): boolean {
  return !!key && DOMAIN_KEYS.has(key)
}

/**
 * A domain's own hue, or a caller-chosen fallback when the agent invents a
 * domain we have no colour for. Colour is never a raw value — it resolves to a
 * token so both themes stay correct.
 */
export function domainColor(key?: string, fallback = 'var(--line-strong)'): string {
  return isKnownDomain(key) ? `var(--domain-${key})` : fallback
}

export function frameworkDomain(key?: string): DomainDef | undefined {
  return DOMAINS.find((d) => d.key === key)
}

/**
 * A domain key as a parent would read it. A key the framework has never heard
 * of keeps its own name with the camelCase split out, so a domain the agent
 * invents still reads as words instead of disappearing.
 */
export function domainLabel(key?: string): string {
  if (!key) return ''
  const known = frameworkDomain(key)?.name
  if (known) return known
  const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/** Every domain the framework knows, in presentation order. */
export function frameworkDomains(): DomainDef[] {
  return DOMAINS
}

/**
 * Framework skills, with `key` normalized to the FULL id ("logic.deduction").
 * The framework's own `key` field is the short form ("deduction"), but every
 * spine row and every URL carries the full id — one vocabulary everywhere.
 */
export function frameworkSkills(domainKey?: string): Skill[] {
  if (!isKnownDomain(domainKey)) return []
  return getSkillsForDomain(domainKey as DomainKey).map(
    (s) => ({ ...s, key: s._id as string }) as Skill,
  )
}

export function frameworkSkill(domainKey?: string, skillKey?: string): Skill | undefined {
  if (!skillKey) return undefined
  return frameworkSkills(domainKey).find(
    (s) => (s._id as string) === skillKey || s.key === skillKey,
  )
}

/** "Proficient" — the name of a whole level, independent of skill. */
export function levelLabel(level?: number): string | undefined {
  const l = clampLevel(level)
  return l ? LEVEL_LABELS[l] : undefined
}

export function clampLevel(level?: number): Level | undefined {
  if (typeof level !== 'number' || Number.isNaN(level)) return undefined
  const rounded = Math.round(level)
  if (rounded < 1) return 1
  if (rounded > 5) return 5
  return rounded as Level
}

/**
 * What this level means *for this skill* — the sentence that makes a number
 * mean something. Undefined when the skill is outside the framework, which the
 * UI shows as an honest gap rather than inventing a description.
 */
export function levelDescriptor(
  domainKey?: string,
  skillKey?: string,
  level?: number,
): LevelDescriptor | undefined {
  const l = clampLevel(level)
  if (!l) return undefined
  return frameworkSkill(domainKey, skillKey)?.levelDescriptors.find((d) => d.level === l)
}

/** The next rung — always framed as what is coming, never as what is missing. */
export function nextLevelDescriptor(
  domainKey?: string,
  skillKey?: string,
  level?: number,
): LevelDescriptor | undefined {
  const l = clampLevel(level)
  if (!l || l >= 5) return undefined
  return levelDescriptor(domainKey, skillKey, l + 1)
}

export interface PrerequisiteRef {
  id: string
  name: string
  domainKey?: string
}

/** Prerequisites resolved to readable names; unknown ids keep their raw id. */
export function prerequisites(domainKey?: string, skillKey?: string): PrerequisiteRef[] {
  const skill = frameworkSkill(domainKey, skillKey)
  if (!skill) return []
  return skill.prerequisites.map((id) => {
    const found = getSkill(id)
    return { id, name: found?.name ?? id, domainKey: found?.domainKey }
  })
}

export function assessmentHints(domainKey?: string, skillKey?: string): string[] {
  return frameworkSkill(domainKey, skillKey)?.assessmentHints ?? []
}

export { LEVEL_LABELS }
export type { Level, LevelDescriptor }
